import React, { useCallback, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import U2000ComboBoxBase from '../../../formControls/combobox/U2000ComboBoxBase';
import { U2000_COMBO_BOX_NONE_ITEM_KEY } from '../../../../tools/u2000ComboBoxCommitUtils';
import type { U2000CriteriaStaticOption } from '../../basic/U2000BasicTableList';
import type { U2000CriteriaTag } from './u2000CriteriaTags';

const TAG_BASE =
  'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-150 max-w-xs bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100';
/** Une seule boîte : tags + ComboBox intégré (hauteur alignée autres critères — 46px). */
const CONTAINER_BASE =
  'flex w-full min-h-[46px] flex-wrap items-center gap-1.5 px-4 py-2 text-sm bg-surface border rounded-lg transition-all duration-200 border-gray-300 hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20';

const MAX_TAGS = 50;

interface U2000CriteriaOptionsComboInProps {
  options: U2000CriteriaStaticOption[];
  value: U2000CriteriaTag[];
  onChange: (tags: U2000CriteriaTag[]) => void;
  disabled?: boolean;
  placeholder?: string;
  onAfterSelect?: () => void;
}

/**
 * Opérateur Parmi sur liste fermée : ComboBox intégré + tags (BL-0067).
 */
const U2000CriteriaOptionsComboIn: React.FC<U2000CriteriaOptionsComboInProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  placeholder,
  onAfterSelect,
}) => {
  const [comboKey, setComboKey] = useState(0);
  const optionByValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
  const selectedKeys = useMemo(() => new Set(value.map((t) => t.value)), [value]);

  const availableOptions = useMemo(
    () => options.filter((o) => !selectedKeys.has(o.value)),
    [options, selectedKeys],
  );

  const removeTag = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  const addOption = useCallback(
    (opt: U2000CriteriaStaticOption | null) => {
      if (!opt?.value || opt.value === U2000_COMBO_BOX_NONE_ITEM_KEY || selectedKeys.has(opt.value)) return;
      if (value.length >= MAX_TAGS) return;
      onChange([...value, { value: opt.value, label: opt.label }]);
      setComboKey((k) => k + 1);
      window.setTimeout(() => onAfterSelect?.(), 0);
    },
    [value, onChange, selectedKeys, onAfterSelect],
  );

  const comboDisabled = disabled || value.length >= MAX_TAGS || availableOptions.length === 0;

  return (
    <div className={`${CONTAINER_BASE} ${disabled ? 'cursor-not-allowed bg-muted opacity-70' : ''}`}>
      {value.map((tag, idx) => (
        <span
          key={`${tag.value}-${idx}`}
          className={TAG_BASE}
          title={tag.label ?? tag.value}
          aria-label={`Retirer ${tag.label ?? tag.value}`}
        >
          <span className="truncate">{tag.label ?? optionByValue.get(tag.value)?.label ?? tag.value}</span>
          {!disabled && (
            <button
              type="button"
              className="rounded-full p-0.5 hover:bg-blue-200/80"
              onClick={() => removeTag(idx)}
              aria-label={`Retirer ${tag.label ?? tag.value}`}
            >
              <FaTimes className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      <div className="min-w-40 flex-1">
        <U2000ComboBoxBase<U2000CriteriaStaticOption>
          key={comboKey}
          embedded
          data={availableOptions}
          keyField="value"
          descField="label"
          value={null}
          placeHolder={placeholder ?? ''}
          onChangeObject={addOption}
          disabled={comboDisabled}
        />
      </div>
    </div>
  );
};

export default U2000CriteriaOptionsComboIn;
